/**
 * feAI – Frontend
 * src/hooks/usePageTracking.ts
 *
 * Hook che traccia automaticamente la navigazione dell'utente tra le pagine
 * del portale, inviando una segnalazione al backend (page-access/log).
 *
 * Utilizzo:
 *   In App.tsx, all'interno del BrowserRouter:
 *     import { PageTrackingProvider } from './hooks/usePageTracking';
 *     <PageTrackingProvider />
 *
 * Ad ogni cambio di pathname (React Router), viene chiamata l'API
 * POST /api/v1/core/page-access/log con i dati della pagina visitata.
 *
 * La mappa pageLabelMap associa le route alle label del menu.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../lib/apiClient';

/**
 * Mappa: route → label del menu / descrizione pagina.
 * Mantenuta allineata con i NavItem in AppShell.tsx.
 */
const PAGE_LABEL_MAP: Record<string, string> = {
  'dashboard':          'Dashboard',
  'agenti-dashboard':   'Dashboard Agenti',
  'users':              'Utenti',
  'report-apparound':   'Report Apparound',
  'report-accessi':     'Report Accessi',
  'page-access-report': 'Navigazione Pagine',
  'archiviati':         'Archiviati',
  'archiviati/tabs':    'Archiviati - Configurazione Tab',
  'admin/dettaglio-tabs': 'Configurazione Dettaglio',
  'retention':          'Retention',
  'admin/permissions':  'Ruoli e Permessi',
  'partite-aperte':     'Partite Aperte',
  'mail-report':        'Mail Report',
  'sso-auth':           'SSO Auth',
  'forgot-password':    'Recupero Password',
  'reset-password':     'Reset Password',
  'login':              'Login',
  'brand/catalog':      'Brand Catalog',
};

/**
 * Estrae l'identificativo della pagina dal pathname.
 * Esempio: "/dashboard?foo=bar" → "dashboard"
 *          "/archiviati/tabs"  → "archiviati/tabs"
 *          "/admin/permissions" → "admin/permissions"
 */
function extractPageFromPath(pathname: string): string {
  // Toglie lo slash iniziale
  return pathname.replace(/^\//, '') || 'home';
}

/**
 * Rileva il tipo di terminale da cui l'utente sta navigando.
 *
 * Priorità:
 *   1. User-Agent con marche mobile (Android, iPhone, iPad, etc.)
 *   2. navigator.platform (Mac on iPad si comporta spesso da desktop)
 *   3. navigator.maxTouchPoints / pointer coarse (touch primario)
 *
 * @returns 'FISSO' (desktop) oppure 'MOBILE'
 */
function detectDeviceType(): 'FISSO' | 'MOBILE' {
  if (typeof navigator === 'undefined') return 'FISSO';

  const ua = navigator.userAgent || '';
  const isMobileUa =
    /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Kindle/i.test(ua);
  const isIPad =
    /iPad/i.test(ua) ||
    (/Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1);

  if (isMobileUa || isIPad) return 'MOBILE';
  if (navigator.maxTouchPoints > 1) return 'MOBILE';
  return 'FISSO';
}

/**
 * Provider da inserire in App.tsx all'interno del BrowserRouter.
 * Ascolta i cambi di route e invia il log al backend.
 */
export const PageTrackingProvider: React.FC = () => {
  const location = useLocation();
  const lastPageRef = useRef<string>('');

  useEffect(() => {
    const page = extractPageFromPath(location.pathname);

    // Evita log doppi per la stessa pagina consecutivamente
    if (page === lastPageRef.current) return;
    lastPageRef.current = page;

    const pageLabel = PAGE_LABEL_MAP[page] ?? null;
    const deviceType = detectDeviceType();

    // Invia la segnalazione in modo asincrono (fire-and-forget)
    apiClient.post('/api/v1/core/page-access/log', {
      page,
      page_label: pageLabel,
      device_type: deviceType,
    }).catch((err) => {
      // Silenzioso: gli errori di tracking non devono disturbare l'utente
      // Log solo in sviluppo
      if (import.meta.env.DEV) {
        console.warn('[PageTracking] Errore logging navigazione:', err.message);
      }
    });
  }, [location.pathname]);

  return null; // Componente invisibile
};

export default PageTrackingProvider;